import React from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { X, Shield, FileText, Cookie, Scissors, ArrowRight } from 'lucide-react';
import { useLanguage } from '../lib/LanguageContext';
import { useTheme } from '../lib/ThemeContext';
import { Logo } from './Logo';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFacebook, faInstagram } from '@fortawesome/free-brands-svg-icons';

interface LegalModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAccept?: () => void;
  type: 'terms' | 'privacy' | 'cookies' | 'status';
}

export const LegalModal: React.FC<LegalModalProps> = ({ isOpen, onClose, onAccept, type }) => {
  const { t } = useLanguage();

  const handleConfirm = () => {
    if (onAccept) {
      onAccept();
    } else {
      onClose();
    }
  };

  const content = {
    terms: {
      title: t('legal.terms'),
      icon: FileText,
      body: `
        <h2>1. Acceptance of Terms</h2>
        <p>By accessing and using this service, you accept and agree to be bound by the terms and provision of this agreement.</p>
        
        <h2>2. Subscription Services</h2>
        <p>The Pro Barber Shop subscription provides access to management tools for a monthly fee of CHF 49.99. Subscriptions are billed in advance on a monthly basis.</p>
        
        <h2>3. User Responsibilities</h2>
        <p>Users are responsible for maintaining the confidentiality of their account and password. You agree to accept responsibility for all activities that occur under your account.</p>
        
        <h2>4. Cancellation and Refunds</h2>
        <p>You may cancel your subscription at any time. Refunds are not provided for partial months of service.</p>
        
        <h2>5. Limitation of Liability</h2>
        <p>We shall not be liable for any indirect, incidental, special, consequential or punitive damages resulting from your use of the service.</p>
      `
    },
    privacy: {
      title: t('legal.privacy'),
      icon: Shield,
      body: `
        <h2>1. Data Collection</h2>
        <p>We collect information you provide directly to us, such as when you create an account, subscribe to our service, or communicate with us.</p>
        
        <h2>2. Use of Information</h2>
        <p>We use the information we collect to provide, maintain, and improve our services, and to process your subscription payments.</p>
        
        <h2>3. Data Sharing</h2>
        <p>We do not share your personal information with third parties except as described in this policy (e.g., with payment processors like Stripe).</p>
        
        <h2>4. Data Security</h2>
        <p>We take reasonable measures to help protect information about you from loss, theft, misuse and unauthorized access.</p>
      `
    },
    cookies: {
      title: t('legal.cookies'),
      icon: Cookie,
      body: `
        <h2>1. What are Cookies</h2>
        <p>Cookies are small text files that are placed on your computer by websites that you visit. They are widely used in order to make websites work, or work more efficiently.</p>
        
        <h2>2. How we use Cookies</h2>
        <p>We use cookies for authentication, session management, and to remember your preferences (like language settings).</p>
        
        <h2>3. Third-party Cookies</h2>
        <p>We use Stripe for payment processing, which may set its own cookies to ensure secure transactions.</p>
        
        <h2>4. Managing Cookies</h2>
        <p>Most web browsers allow some control of most cookies through the browser settings.</p>
      `
    },
    status: {
      title: t('admin.status') || 'System Status',
      icon: Shield,
      body: `
        <h2>System Performance</h2>
        <p>${t('admin.platformHealthDesc') || 'Your platform is currently performing at optimal levels. All systems are operational.'}</p>
        
        <h2>Recent Updates</h2>
        <p>Our platform is continuously monitored and updated to ensure maximum reliability and performance for all barber shops and customers.</p>
        
        <h2>Security</h2>
        <p>Security is our top priority. We use industry-standard encryption and security protocols to protect your data.</p>
      `
    }
  };

  const active = content[type];
  const Icon = active.icon;

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 md:p-6">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="absolute inset-0 bg-black/80 backdrop-blur-sm"
          />
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            className="relative w-full max-w-2xl bg-zinc-900 border border-zinc-800 rounded-[2.5rem] shadow-2xl overflow-hidden flex flex-col max-h-[80vh]"
          >
            <div className="p-8 border-b border-zinc-800 flex items-center justify-between shrink-0">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-amber-500/10 rounded-2xl flex items-center justify-center">
                  <Icon className="text-amber-500" size={24} />
                </div>
                <h2 className="text-2xl font-black uppercase tracking-tighter text-white">{active.title}</h2>
              </div>
              <button 
                onClick={onClose}
                aria-label={t('common.close')}
                className="p-3 hover:bg-zinc-800 rounded-xl transition-colors text-zinc-400 hover:text-white"
              >
                <X size={20} aria-hidden="true" />
              </button>
            </div>
            
            <div className="p-8 overflow-y-auto custom-scrollbar prose prose-invert prose-amber max-w-none text-zinc-300">
              <div 
                className="[&>h2]:text-white [&>h2]:text-lg [&>h2]:font-black [&>h2]:uppercase [&>h2]:tracking-tight [&>h2]:mb-4 [&>h2]:mt-8 [&>p]:text-zinc-400 [&>p]:leading-relaxed [&>p]:mb-4" 
                dangerouslySetInnerHTML={{ __html: active.body }} 
              />
            </div>
            
            <div className="p-6 bg-zinc-950/50 border-t border-zinc-800 flex justify-end shrink-0">
              <button
                onClick={handleConfirm}
                className="px-8 py-3 bg-white text-black rounded-xl font-black uppercase tracking-widest hover:bg-amber-500 hover:text-white transition-all"
              >
                {t('common.confirm')}
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};

export const Footer: React.FC<{ 
  variant?: 'light' | 'dark'; 
  onLogoClick?: () => void;
  onViewPricing?: () => void;
}> = ({ variant, onLogoClick, onViewPricing }) => {
  const { t } = useLanguage();
  const { theme } = useTheme();
  const [legalType, setLegalType] = React.useState<'terms' | 'privacy' | 'cookies' | 'status' | null>(null);
  
  // isDarkBackground means we display on a dark bg (black/zinc-950)
  const isDarkBackground = variant ? variant === 'dark' : theme === 'dark';

  return (
    <footer className={`mt-24 border-t transition-all duration-300 pt-24 pb-16 relative overflow-hidden ${
      isDarkBackground 
        ? 'border-zinc-800 bg-black text-white' 
        : 'border-zinc-100 bg-zinc-50/50 text-zinc-900'
    }`}>
      {/* Decorative gradient for light mode */}
      {!isDarkBackground && (
        <div className="absolute inset-0 bg-gradient-to-b from-white to-zinc-50/50 pointer-events-none" />
      )}
      
      <LegalModal 
        isOpen={!!legalType} 
        onClose={() => setLegalType(null)} 
        type={legalType || 'terms'} 
      />
      <div className="max-w-7xl mx-auto px-6 relative z-10">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-16 mb-20">
          {/* Logo & About */}
          <div className="md:col-span-4 flex flex-col items-start gap-8">
            <Logo 
              variant={isDarkBackground ? 'light' : 'dark'} 
              className="transform -translate-x-1"
              onClick={onLogoClick}
            />
            <p className={`text-sm font-medium leading-relaxed ${
              isDarkBackground ? 'text-zinc-400' : 'text-zinc-500'
            }`}>
              {t('common.est') || 'Modern barbering excellence since 2024. We combine traditional craftsmanship with contemporary style to deliver more than just a cut — we deliver an experience.'}
            </p>
            <div className="flex gap-4">
              <a 
                href="https://www.facebook.com/profile.php?id=61589617326203" 
                target="_blank" 
                rel="noopener noreferrer"
                className={`w-10 h-10 rounded-xl border flex items-center justify-center transition-all cursor-pointer ${
                  isDarkBackground 
                    ? 'border-zinc-800 hover:border-amber-500 text-zinc-500 hover:text-white bg-white/5' 
                    : 'border-zinc-200 hover:border-zinc-900 text-zinc-400 hover:text-zinc-900 bg-white shadow-sm'
                }`}
              >
                <FontAwesomeIcon icon={faFacebook} className="text-lg" />
              </a>
              <a 
                href="https://instagram.com" 
                target="_blank" 
                rel="noopener noreferrer"
                className={`w-10 h-10 rounded-xl border flex items-center justify-center transition-all cursor-pointer ${
                  isDarkBackground 
                    ? 'border-zinc-800 hover:border-amber-500 text-zinc-500 hover:text-white bg-white/5' 
                    : 'border-zinc-200 hover:border-zinc-900 text-zinc-400 hover:text-zinc-900 bg-white shadow-sm'
                }`}
              >
                <FontAwesomeIcon icon={faInstagram} className="text-lg" />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div className="md:col-span-4 grid grid-cols-2 gap-8">
            <div className="flex flex-col gap-8">
              <h3 className={`text-[10px] font-black uppercase tracking-[0.2em] ${
                isDarkBackground ? 'text-zinc-400' : 'text-zinc-900'
              }`}>
                {t('admin.systemOverview') || 'Legal'}
              </h3>
              <div className="flex flex-col gap-4">
                {[
                  { id: 'terms', label: t('common.terms') },
                  { id: 'privacy', label: t('common.privacy') },
                  { id: 'cookies', label: t('common.cookies') }
                ].map(link => (
                  <button 
                    key={link.id}
                    onClick={() => setLegalType(link.id as any)}
                    className={`flex items-center gap-2 text-xs font-bold uppercase tracking-widest transition-all w-fit group ${
                      isDarkBackground ? 'text-zinc-500 hover:text-white' : 'text-zinc-400 hover:text-zinc-900'
                    }`}
                  >
                    <span className="relative pb-1">
                      {link.label}
                      <span className="absolute bottom-0 left-0 w-0 h-px bg-amber-500 transition-all duration-300 group-hover:w-full" />
                    </span>
                    <ArrowRight size={14} className="opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all text-amber-500" />
                  </button>
                ))}
              </div>
            </div>

            <div className="flex flex-col gap-8">
              <h3 className={`text-[10px] font-black uppercase tracking-[0.2em] ${
                isDarkBackground ? 'text-zinc-400' : 'text-zinc-900'
              }`}>
                Platform
              </h3>
              <div className="flex flex-col gap-4">
                {[
                  { id: 'about', label: t('common.about') || 'About', action: () => window.scrollTo({ top: 0, behavior: 'smooth' }) },
                  { id: 'pricing', label: t('nav.becomePartner') || 'Become a Partner', action: onViewPricing },
                  { id: 'shops', label: t('admin.shops') || 'Shops', action: onLogoClick },
                  { id: 'contact', label: t('common.contact') || 'Contact', action: () => setLegalType('status') }
                ].map(link => (
                  <button 
                    key={link.label}
                    onClick={link.action}
                    className={`flex items-center gap-2 text-xs font-bold uppercase tracking-widest transition-all w-fit group ${
                      isDarkBackground ? 'text-zinc-500 hover:text-white' : 'text-zinc-400 hover:text-zinc-900'
                    }`}
                  >
                    <span className="relative pb-1">
                      {link.label}
                      <span className="absolute bottom-0 left-0 w-0 h-px bg-amber-500 transition-all duration-300 group-hover:w-full" />
                    </span>
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Contact / Newsletter Area */}
          <div className="md:col-span-4 flex flex-col gap-8">
            <h3 className={`text-[10px] font-black uppercase tracking-[0.2em] ${
              isDarkBackground ? 'text-zinc-400' : 'text-zinc-900'
            }`}>
              Our Commitment
            </h3>
            <div className={`p-8 rounded-[2rem] border relative overflow-hidden group ${
              isDarkBackground 
                ? 'bg-zinc-900/50 border-zinc-800' 
                : 'bg-white border-zinc-100 shadow-xl shadow-zinc-200/50'
            }`}>
              <div className="absolute top-0 right-0 p-4 opacity-5 group-hover:scale-110 transition-transform duration-700">
                <Scissors size={80} />
              </div>
              <p className={`text-sm font-medium leading-relaxed italic relative z-10 ${
                isDarkBackground ? 'text-zinc-400' : 'text-zinc-500'
              }`}>
                "Every client is distinct, every service is personal. We don't just cut hair; we craft identity."
              </p>
              <div className="mt-6 flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-amber-500 flex items-center justify-center text-white font-black text-xs">SC</div>
                <div>
                  <p className={`text-[10px] font-black uppercase tracking-tight ${isDarkBackground ? 'text-white' : 'text-zinc-900'}`}>Sharp Cuts Team</p>
                  <p className="text-[8px] font-bold uppercase tracking-widest text-amber-500">Premium Network</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className={`pt-12 border-t flex flex-col lg:flex-row items-center justify-between gap-8 ${
          isDarkBackground ? 'border-zinc-800/50' : 'border-zinc-200/50'
        }`}>
          <div className="flex flex-col md:flex-row items-center gap-6 md:gap-10">
            <p className={`text-[10px] font-black uppercase tracking-[0.2em] ${
              isDarkBackground ? 'text-zinc-500' : 'text-zinc-400'
            }`}>
              {t('common.copyright').replace('{year}', new Date().getFullYear().toString())}
            </p>
            <div className="flex gap-6">
              {[
                { id: 'status', label: t('admin.status') || 'Status' },
                { id: 'privacy', label: t('common.privacy') || 'Privacy' },
                { id: 'terms', label: t('common.terms') || 'Terms' }
              ].map(link => (
                <button 
                  key={link.id} 
                  onClick={() => setLegalType(link.id as any)}
                  className="text-[9px] font-black uppercase tracking-widest text-zinc-500 hover:text-amber-500 transition-colors"
                >
                  {link.label}
                </button>
              ))}
            </div>
          </div>

          <div className="flex items-center gap-6">
             <p className={`text-[9px] font-black uppercase tracking-widest px-5 py-2.5 rounded-full border shadow-sm ${
               isDarkBackground 
                ? 'border-zinc-800 bg-zinc-900/50 text-zinc-500' 
                : 'border-zinc-100 bg-white text-zinc-400'
             }`}>
               Switzerland • Premium Network
             </p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export const CookieConsent: React.FC = () => {
  const { t } = useLanguage();
  const [isVisible, setIsVisible] = React.useState(false);

  React.useEffect(() => {
    const consent = localStorage.getItem('cookie-consent');
    if (!consent) {
      setIsVisible(true);
    }
  }, []);

  const handleAccept = () => {
    localStorage.setItem('cookie-consent', 'true');
    setIsVisible(false);
  };

  if (!isVisible) return null;

  return (
    <motion.div
      initial={{ y: 100, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      className="fixed bottom-6 left-6 right-6 md:left-auto md:right-6 md:max-w-md z-[90]"
    >
      <div className="bg-zinc-900 border border-zinc-800 p-6 rounded-2xl shadow-2xl backdrop-blur-xl">
        <div className="flex items-start gap-4 mb-6">
          <div className="w-10 h-10 bg-amber-500/10 rounded-xl flex items-center justify-center shrink-0">
            <Cookie className="text-amber-500" size={20} aria-hidden="true" />
          </div>
          <div>
            <h3 className="font-black uppercase tracking-tight text-white mb-1">{t('legal.cookies')}</h3>
            <p className="text-xs text-zinc-400 font-medium leading-relaxed">
              {t('legal.cookieConsent')}
            </p>
          </div>
        </div>
        <div className="flex gap-3">
          <button
            onClick={handleAccept}
            className="flex-1 py-3 bg-white text-black rounded-xl font-black uppercase tracking-widest text-[10px] hover:bg-amber-500 hover:text-white transition-all"
          >
            {t('legal.accept')}
          </button>
          <button
            onClick={() => setIsVisible(false)}
            className="px-6 py-3 bg-zinc-800 text-zinc-400 rounded-xl font-black uppercase tracking-widest text-[10px] hover:bg-zinc-700 hover:text-white transition-all"
          >
            {t('legal.decline')}
          </button>
        </div>
      </div>
    </motion.div>
  );
};
